import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import Footer from "./Footer";
import debounce from "lodash/debounce";
import Header from "./Header";

function AddProduct() {
  const { procode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const dealer_id = localStorage.getItem("dealer_id");
  const server_url = localStorage.getItem("server_url");
  const LoginNumber = localStorage.getItem("LoginNumber");

  const [qrValue, setQrValue] = useState(procode || "");
  const [searchTerm, setSearchTerm] = useState("");
  const [batches, setBatches] = useState({});
  // displayBatches stores the batch keys that are currently shown on screen
  const [displayBatches, setDisplayBatches] = useState([]);
  const [loadedPages, setLoadedPages] = useState(new Set());
  const [allPageKeys, setAllPageKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [addingProducts, setAddingProducts] = useState(new Map());
  const [cartCount, setCartCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);
  const productRefs = useRef({});
  const searchInputRef = useRef(null);
  const [showSteppers, setShowSteppers] = useState(new Map());
  const [quantityInputs, setQuantityInputs] = useState({});
  const hasScrolled = useRef(false);
  const [showSearch, setShowSearch] = useState(false);
  const observer = useRef();

  // Store all products for search functionality (so searching doesn't need pagination)
  const [allProducts, setAllProducts] = useState([]);

  // BATCH SIZE: Load products in pages of 10
  const BATCH_SIZE = 10;

  // Dynamic column count
  const getColumnCount = () => {
    const width = window.innerWidth;
    if (width >= 2500) return 7;
    if (width >= 2000) return 6;
    if (width >= 1500) return 5;
    if (width >= 1200) return 4;
    if (width >= 768) return 2;
    return 2;
  };

  const [columnCount, setColumnCount] = useState(getColumnCount());

  useEffect(() => {
    const handleResize = () => {
      setColumnCount(getColumnCount());
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update URL with search parameters
  const updateURLWithSearch = useCallback((searchValue) => {
    const url = new URL(window.location);

    if (searchValue && searchValue.trim() !== "") {
      url.searchParams.set("search", searchValue.trim());
    } else {
      url.searchParams.delete("search");
    }

    window.history.replaceState({}, "", url.toString());
  }, []);

  // Auto-open search bar and set search term when URL has search parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(search);
    const urlSearch = urlParams.get("search");
    if (urlSearch) {
      setSearchTerm(urlSearch);
      setShowSearch(true); // Auto-open search bar
      setQrValue("");
    }
  }, [search]);

  const isValidLocalCart = useCallback((localData) => {
    return (
      Array.isArray(localData) &&
      localData.every(
        (item) =>
          item.product_id &&
          item.product_design_name &&
          item.product_image &&
          item.product_quantity !== undefined &&
          item.product_dealer_price !== undefined &&
          item.series_id !== undefined &&
          item.product_qr_value !== undefined,
      )
    );
  }, []);

  const getLocalCart = useCallback(() => {
    const localCart = localStorage.getItem("local_cart");
    try {
      const parsed = localCart ? JSON.parse(localCart) : [];
      return isValidLocalCart(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }, [isValidLocalCart]);

  const saveLocalCart = useCallback((products) => {
    localStorage.setItem("local_cart", JSON.stringify(products));
  }, []);

  // Fetch ALL paginated chunks once
  const fetchAllProducts = useCallback(async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("company_id", "2");
      formData.append("dealer_id", dealer_id || "");

      const res = await axios.post(
        `${server_url}products_with_cart_web.php`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      // console.log(dealer_id);
      // console.log(res.data);

      if (res.data.status === "1" && res.data.result) {
        const resultObj = res.data.result;
        const pageKeys = Object.keys(resultObj)
          .filter((k) => !isNaN(Number(k)))
          .sort((a, b) => Number(a) - Number(b));

        setAllPageKeys(pageKeys);
        setBatches(resultObj);

        // Load the first batch (Page 1) initially
        const firstKey = pageKeys[0];
        if (firstKey) {
          setDisplayBatches([firstKey]);
          setLoadedPages(new Set([firstKey]));
        } else {
          setDisplayBatches([]);
          setLoadedPages(new Set());
        }

        // Store all products for search functionality
        const allProductsData = Object.values(resultObj).flat();
        setAllProducts(allProductsData);
        saveLocalCart(allProductsData);
        setCartTotal(parseFloat(res.data.totalAmount) || 0);

        const qtyMap = allProductsData.reduce(
          (acc, item) => ({ ...acc, [item.product_id]: item.product_quantity }),
          {},
        );
        setQuantityInputs(qtyMap);

        const steppersMap = new Map();
        allProductsData.forEach((item) => {
          steppersMap.set(item.product_id, item.product_quantity > 0);
        });
        setShowSteppers(steppersMap);

        setCartCount(
          allProductsData.filter((i) => i.product_quantity > 0).length,
        );
      } else {
        setDisplayBatches([]);
        setAllProducts([]);
        toast.warning(res.data.error || "No products");
      }
    } catch (e) {
      toast.error("Failed to load products.");
      setDisplayBatches([]);
      setAllProducts([]);
    } finally {
      setIsLoading(false);
    }
  }, [dealer_id, server_url, saveLocalCart]);

  // Initial load
  useEffect(() => {
    const localData = getLocalCart();
    if (localData.length > 0 && !procode) {
      // Reconstruct batches from local data based on BATCH_SIZE
      const batchesFromLocal = {};
      const totalProducts = localData.length;
      const totalPages = Math.ceil(totalProducts / BATCH_SIZE);

      for (let i = 0; i < totalPages; i++) {
        const page = i + 1;
        const start = i * BATCH_SIZE;
        const end = start + BATCH_SIZE;
        batchesFromLocal[page] = localData.slice(start, end);
      }

      const keys = Object.keys(batchesFromLocal).sort(
        (a, b) => Number(a) - Number(b),
      );

      setAllPageKeys(keys);
      setBatches(batchesFromLocal);

      // Load first batch only
      const firstKey = keys[0];
      if (firstKey) {
        setDisplayBatches([firstKey]);
        setLoadedPages(new Set([firstKey]));
      }

      // Store all products for search
      setAllProducts(localData);

      const qtyMap = localData.reduce(
        (acc, i) => ({ ...acc, [i.product_id]: i.product_quantity }),
        {},
      );
      setQuantityInputs(qtyMap);
      const steppers = new Map();
      localData.forEach((i) =>
        steppers.set(i.product_id, i.product_quantity > 0),
      );
      setShowSteppers(steppers);
      setCartCount(localData.filter((i) => i.product_quantity > 0).length);
      setIsLoading(false);
    } else {
      fetchAllProducts();
    }
  }, [procode, getLocalCart, fetchAllProducts]);

  useEffect(() => {
    fetchAllProducts();
  }, []);

  // Load next batch
  const loadNextBatch = useCallback(() => {
    // If searching, we don't paginate via scroll (search shows all filtered results)
    if (searchTerm) return;

    if (isLoadingMore || loadedPages.size >= allPageKeys.length) return;

    const nextIndex = loadedPages.size; // 0-based index, so size is the next index
    const nextKey = allPageKeys[nextIndex];

    // Ensure we haven't loaded this key already
    if (!nextKey || loadedPages.has(nextKey)) return;

    setIsLoadingMore(true);

    // Small timeout to allow UI to show loading state if needed
    setTimeout(() => {
      setDisplayBatches((prev) => [...prev, nextKey]);
      setLoadedPages((prev) => new Set([...prev, nextKey]));
      setIsLoadingMore(false);
    }, 300);
  }, [allPageKeys, loadedPages, isLoadingMore, searchTerm]);

  // Get all products as a single flat array for display based on loaded batches
  const allDisplayProducts = useMemo(() => {
    // Map through the loaded batch keys and flatten their products
    return displayBatches.flatMap((key) => batches[key] || []);
  }, [displayBatches, batches]);

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return allDisplayProducts;

    // If we have search term, search through ALL products, not just displayed ones
    return allProducts.filter((p) =>
      p.product_design_name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [allDisplayProducts, searchTerm, allProducts]);

  // Distribute products column by column (vertically)
  const distributeProductsByColumns = useCallback((products, cols) => {
    if (products.length === 0 || cols === 0) return [];

    const columns = Array.from({ length: cols }, () => []);

    // Distribute products vertically (column by column)
    for (let i = 0; i < products.length; i++) {
      const colIndex = i % cols;
      columns[colIndex].push(products[i]);
    }

    return columns;
  }, []);

  const productColumns = useMemo(() => {
    return distributeProductsByColumns(filteredProducts, columnCount);
  }, [filteredProducts, columnCount, distributeProductsByColumns]);

  // Observer for infinite scroll - only when not searching
  const lastProductRef = useCallback(
    (node) => {
      if (isLoading || isLoadingMore || searchTerm) return; // Don't load more when searching or initial loading

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            loadedPages.size < allPageKeys.length
          ) {
            loadNextBatch();
          }
        },
        {
          rootMargin: window.innerWidth > 1500 ? "1200px" : "600px",
        },
      );

      if (node) observer.current.observe(node);
    },
    [
      isLoading,
      isLoadingMore,
      loadedPages.size,
      allPageKeys.length,
      loadNextBatch,
      searchTerm,
    ],
  );

  // QR & hash scroll
  useEffect(() => {
    if (isLoading || filteredProducts.length === 0) return;
    const matched = filteredProducts.find(
      (item) => item.product_qr_value.toLowerCase() === qrValue.toLowerCase(),
    );
    if (matched && productRefs.current[matched.product_id]) {
      productRefs.current[matched.product_id].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [qrValue, filteredProducts, isLoading]);

  useEffect(() => {
    if (isLoading || filteredProducts.length === 0 || hasScrolled.current)
      return;
    const hash = window.location.hash.replace("#", "");
    if (hash && productRefs.current[hash]) {
      productRefs.current[hash].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      hasScrolled.current = true;
    }
  }, [filteredProducts, isLoading]);

  // Add to cart
  const addToCart = useCallback(
    async (productId, qty) => {
      if (!LoginNumber || LoginNumber.trim() === "" || !dealer_id) {
        const currentPath = location.pathname + location.search;
        localStorage.setItem("login_url", currentPath);
        navigate("/Logout");
        return;
      }

      if (!productId || qty < 0 || isNaN(qty)) return false;

      setAddingProducts((prev) => new Map(prev).set(productId, true));
      try {
        const formData = new FormData();
        formData.append("company_id", "2");
        formData.append("dealer_id", dealer_id || "");
        formData.append("product_id", productId);
        formData.append("qty", qty);

        const res = await axios.post(`${server_url}add_cart.php`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (res.data.status === "1") {
          // Update the specific product in the main state (allProducts)
          const updatedAllProducts = allProducts.map((item) =>
            item.product_id === productId
              ? { ...item, product_quantity: qty }
              : item,
          );
          setAllProducts(updatedAllProducts);

          // Update the specific product in the batches object
          setBatches((prevBatches) => {
            const newBatches = {};
            Object.keys(prevBatches).forEach((key) => {
              newBatches[key] = prevBatches[key].map((item) =>
                item.product_id === productId
                  ? { ...item, product_quantity: qty }
                  : item,
              );
            });
            return newBatches;
          });

          saveLocalCart(updatedAllProducts);
          setCartCount(
            updatedAllProducts.filter((i) => i.product_quantity > 0).length,
          );
          setCartTotal(
            updatedAllProducts.reduce(
              (acc, i) => acc + i.product_quantity * i.product_dealer_price,
              0,
            ),
          );
          return true;
        } else {
          toast.warning(res.data.error);
          return false;
        }
      } catch (e) {
        toast("Try again later.");
        return false;
      } finally {
        setAddingProducts((prev) => {
          const m = new Map(prev);
          m.delete(productId);
          return m;
        });
      }
    },
    [dealer_id, server_url, allProducts, saveLocalCart, navigate],
  );

  const debouncedAddToCart = useCallback(debounce(addToCart, 500), [addToCart]);

  const handleQuantityChange = (productId, value) => {
    const qty = value === "" ? "" : parseInt(value, 10);
    setQuantityInputs((prev) => ({ ...prev, [productId]: value }));
    setShowSteppers((prev) => new Map(prev).set(productId, true));
    if (value === "" || (!isNaN(qty) && qty >= 0 && qty <= 99)) {
      if (qty === 0) {
        setShowSteppers((prev) => new Map(prev).set(productId, false));
        debouncedAddToCart(productId, 0);
      } else if (!isNaN(qty)) {
        debouncedAddToCart(productId, qty);
      }
    } else {
      toast("Quantity 0–99 only");
    }
  };

  const handleButtonHold = useCallback(
    (productId, action) => {
      let interval;
      const timeout = setTimeout(() => {
        interval = setInterval(() => {
          setQuantityInputs((prev) => {
            const current = parseInt(prev[productId] || 0, 10);
            const newQty =
              action === "increment"
                ? Math.min(current + 1, 99)
                : Math.max(current - 1, 0);
            setShowSteppers((prevMap) =>
              new Map(prevMap).set(productId, newQty > 0),
            );
            debouncedAddToCart(productId, newQty);
            return { ...prev, [productId]: newQty };
          });
        }, 200);
      }, 500);

      const stop = () => {
        clearTimeout(timeout);
        clearInterval(interval);
        document.removeEventListener("mouseup", stop);
        document.removeEventListener("touchend", stop);
      };
      document.addEventListener("mouseup", stop);
      document.addEventListener("touchend", stop);
    },
    [debouncedAddToCart],
  );

  const handleButtonClick = useCallback(
    (productId, action) => {
      setQuantityInputs((prev) => {
        const current = parseInt(prev[productId] || 0, 10);
        const newQty =
          action === "increment"
            ? Math.min(current + 1, 99)
            : Math.max(current - 1, 0);
        setShowSteppers((prevMap) =>
          new Map(prevMap).set(productId, newQty > 0),
        );
        debouncedAddToCart(productId, newQty);
        return { ...prev, [productId]: newQty };
      });
    },
    [debouncedAddToCart],
  );

  useEffect(() => {
  if (isLoading || searchTerm) return;
  
  // On large screens, load more initial batches
  if (window.innerWidth >= 1500 && displayBatches.length === 1 && allPageKeys.length > 1) {
    // Load first 3 batches on large screens
    const batchesToLoad = Math.min(3, allPageKeys.length);
    const newBatches = allPageKeys.slice(0, batchesToLoad);
    
    if (newBatches.length > 1) {
      setDisplayBatches(newBatches);
      setLoadedPages(new Set(newBatches));
    }
  }
}, [isLoading, displayBatches, allPageKeys, searchTerm]);
  const handleAddClick = (productId) => {
    setQuantityInputs((prev) => ({ ...prev, [productId]: 1 }));
    setShowSteppers((prev) => new Map(prev).set(productId, true));
    debouncedAddToCart(productId, 1);
  };

  const handleProductClick = useCallback((productId) => {
    sessionStorage.setItem("last_visited_product", productId);
  }, []);

  const handleSearch = useCallback(
    (value) => {
      setSearchTerm(value);
      setQrValue("");
      updateURLWithSearch(value);
    },
    [updateURLWithSearch],
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setQrValue("");
    setShowSearch(false);
    updateURLWithSearch("");
  }, [updateURLWithSearch]);

  const handleSearchIconClick = () => setShowSearch(true);

  // Share functionality for mobile
  const handleShare = useCallback(() => {
    const currentUrl = window.location.href;

    if (navigator.share) {
      navigator
        .share({
          title: "Product Search",
          text: `Check out these products${
            searchTerm ? ` matching "${searchTerm}"` : ""
          }`,
          url: currentUrl,
        })
        .then(() => {
          console.log("Successful share");
          toast.success("Shared successfully!");
        })
        .catch((error) => {
          console.log("Error sharing:", error);
          copyToClipboard(currentUrl);
        });
    } else {
      copyToClipboard(currentUrl);
    }
  }, [searchTerm]);

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          toast.success("URL copied to clipboard!");
        })
        .catch(() => {
          const textArea = document.createElement("textarea");
          textArea.value = text;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
          toast.success("URL copied to clipboard!");
        });
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success("URL copied to clipboard!");
    }
  };

  // Auto-focus search input when search is opened
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [showSearch]);

  return (
    <>
      <Header />

      {isLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            // Changed loading background to white
            background: "#ffffff",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: "50px",
              height: "50px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #DB4437", // Red spinner
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
        </div>
      )}

      <div
        className="page-wrapper"
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          position: "relative",
          // Main background set to white
          backgroundColor: "#ffffff",
        }}
      >
        <div
          className="page-content"
          style={{
            flex: "1 0 auto",
            width: "100%",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          {/* Content */}
          <div style={{ paddingTop: isMobile ? "85px":  "20px", minHeight: "calc(100vh - 20px)" }}>
            {!isLoading && filteredProducts.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "calc(100vh - 200px)",
                  // No products text color changed to dark gray
                  color: "#495057",
                  textAlign: "center",
                  padding: "20px",
                }}
              >
                <p style={{ fontSize: "18px", marginBottom: "10px" }}>
                  No products found.
                </p>
                {searchTerm && (
                  <p style={{ fontSize: "14px", opacity: 0.7 }}>
                    No results for "{searchTerm}"
                  </p>
                )}
              </div>
            ) : (
              <div
                className="container"
                style={{ padding: "0 15px", maxWidth: "100%" }}
              >
                {/* SINGLE GRID CONTAINER - Products distributed vertically */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
                    gap: "15px",
                    alignItems: "start",
                  }}
                >
                  {productColumns.map((column, colIndex) => (
                    <div
                      key={colIndex}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "15px",
                      }}
                    >
                      {column.map((product, index) => {
                        const isLastColumn =
                          colIndex === productColumns.length - 1;
                        const isLastProductInColumn =
                          index === column.length - 1;
                        // The last product overall is in the last column, last item of that column
                        const isLastProductOverall =
                          isLastColumn && isLastProductInColumn;

                        return (
                          <div
                            key={product.product_id}
                            ref={(el) => {
                              if (el) {
                                productRefs.current[product.product_id] = el;
                                // Only attach observer if it's the last product AND we aren't searching
                                // AND there are more pages to load
                                if (
                                  isLastProductOverall &&
                                  loadedPages.size < allPageKeys.length &&
                                  !searchTerm
                                ) {
                                  lastProductRef(el);
                                }
                              }
                            }}
                            style={{
                              borderRadius: "12px",
                              position: "relative",
                              padding: "0px",
                              background: "#fff",
                            }}
                          >
                            <div
                              style={{
                                position: "relative",
                                borderRadius: "12px",
                                overflow: "hidden",
                                background: "#f8f9fa", // Light gray placeholder bg
                              }}
                              className="Hoverred"
                            >
                              <Link
                                to={`/ProductDetails/${
                                  product.product_id
                                }/${product.product_design_name
                                  .trim()
                                  .split(/\s+/)
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1).toLowerCase(),
                                  )
                                  .join("")}`}
                                onClick={() =>
                                  handleProductClick(product.product_id)
                                }
                                state={{ from: "AddProduct" }}
                              >
                                <img
                                  src={product.product_image}
                                  alt={product.product_design_name}
                                  style={{
                                    width: "100%",
                                    height: "auto",
                                    objectFit: "cover",
                                    borderRadius: "12px",
                                  }}
                                  loading="lazy"
                                />
                              </Link>

                              {product.alert && (
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "0px",
                                    right: "0px",
                                    background: "#DB4437", // Red alert background
                                    color: "#ffffff",
                                    padding: "0px 7px",
                                    borderRadius: "0px 0px 0px 10px",
                                    fontSize: "12px",
                                  }}
                                >
                                  {product.alert}
                                </div>
                              )}

                              <div
                                style={{
                                  position: "absolute",
                                  bottom: "3px",
                                  right: "3px",
                                }}
                              >
                                {showSteppers.get(product.product_id) ? (
                                  <div
                                    style={{
                                      background: "rgba(255, 255, 255, 0.9)",
                                      backdropFilter: "blur(4px)",
                                      borderRadius: "8px",
                                      display: "flex",
                                      alignItems: "center",
                                      width: "100px",
                                      height: "30px",
                                      border: "1px solid #eee",
                                    }}
                                  >
                                    <button
                                      style={{
                                        padding: "8px",
                                        color: "#333",
                                      }}
                                      onClick={() =>
                                        handleButtonClick(
                                          product.product_id,
                                          "decrement",
                                        )
                                      }
                                      onMouseDown={() =>
                                        handleButtonHold(
                                          product.product_id,
                                          "decrement",
                                        )
                                      }
                                      onTouchStart={() =>
                                        handleButtonHold(
                                          product.product_id,
                                          "decrement",
                                        )
                                      }
                                    >
                                      <i
                                        className="redcolor fa fa-minus"
                                        style={{ color: "#DB4437" }}
                                      ></i>
                                    </button>
                                    <input
                                      type="number"
                                      min="0"
                                      max="99"
                                      value={
                                        quantityInputs[product.product_id] ??
                                        product.product_quantity
                                      }
                                      onChange={(e) =>
                                        handleQuantityChange(
                                          product.product_id,
                                          e.target.value,
                                        )
                                      }
                                      style={{
                                        width: "40px",
                                        textAlign: "center",
                                        background: "transparent",
                                        border: "none",
                                        color: "#212529",
                                      }}
                                    />
                                    <button
                                      style={{ padding: "8px" }}
                                      onClick={() =>
                                        handleButtonClick(
                                          product.product_id,
                                          "increment",
                                        )
                                      }
                                      onMouseDown={() =>
                                        handleButtonHold(
                                          product.product_id,
                                          "increment",
                                        )
                                      }
                                      onTouchStart={() =>
                                        handleButtonHold(
                                          product.product_id,
                                          "increment",
                                        )
                                      }
                                    >
                                      <i
                                        className="redcolor fa fa-plus"
                                        style={{ color: "#DB4437" }}
                                      ></i>
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() =>
                                      handleAddClick(product.product_id)
                                    }
                                    style={{
                                      background: "#DB4437", // Red Add button
                                      color: "white",
                                      border: "none",
                                      borderRadius: "8px",
                                      padding: "5px 10px",
                                    }}
                                  >
                                    <i
                                      className="white fa fa-plus"
                                      style={{ fontSize: "16px" }}
                                    ></i>
                                  </button>
                                )}
                              </div>
                            </div>

                            <Link
                              to={`/ProductDetails/${product.product_id}`}
                              onClick={() =>
                                handleProductClick(product.product_id)
                              }
                              state={{ from: "AddProduct" }}
                            >
                              <p
                                style={{
                                  marginBottom: "5px",
                                  // Product name color changed to dark gray
                                  color: "#343a40",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                }}
                              >
                                {product.product_design_name}
                              </p>
                            </Link>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {/* Loading More - Only show when not searching */}
                {isLoadingMore && !searchTerm && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px 0",
                      gridColumn: `1 / span ${columnCount}`,
                    }}
                  >
                    <div
                      style={{
                        display: "inline-block",
                        width: "30px",
                        height: "30px",
                        border: "3px solid #f3f3f3",
                        borderTop: "3px solid #DB4437",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    ></div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {isMobile ? (
          <>
            {" "}
            <br />
            <br />
            <br />{" "}
          </>
        ) : (
          <br />
        )}

        <div
          style={{
            flexShrink: 0,
            width: "100%",
            marginTop: "auto",
            background: "#ffffff",
          }}
        >
          <Footer />
        </div>
      </div>

      <style jsx>{`
        p {
          font-family: sans-serif;
        }
        .Hoverred:hover {
          border-color: #db4437;
          transform: scale(1.01);
          box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"] {
          -moz-appearance: textfield;
        }
        .redcolor {
          color: rgb(219, 68, 55) !important;
        }
        .white {
          color: rgb(255, 255, 255) !important;
        }
      `}</style>
    </>
  );
}

export default AddProduct;
